import { ApolloClient, NormalizedCacheObject } from '@apollo/client/core'
import { defineStore } from 'pinia'

import { Event } from '__generated__/Event'
import { listEvents } from '__generated__/listEvents'
import { eventAttributesTypes } from '__generated__/eventAttributesTypes'
import { LIST_EVENTS } from '@player/events/api/queries/listEvents'
import { ListEventsFilter, ListEventsSort } from '__generated__/globalTypes'
import { EVENT_ATTRIBUTES_TYPES } from '../api/queries/eventAttributesTypes'

export const useEventsStore = defineStore({
  id: 'events',
  state: () => ({
    events: [] as Event[],
    eventTypes: [] as { label: string; value: string }[],
    count: 0,
    serverError: false,
    loading: false
  }),
  getters: {},
  actions: {
    async listEvents(
      client: ApolloClient<NormalizedCacheObject>,
      filter: ListEventsFilter,
      sort: ListEventsSort[],
      offset: number,
      limit: number
    ) {
      try {
        if (this.loading) return
        this.loading = true

        const { data } = await client.query<listEvents>({
          query: LIST_EVENTS,
          variables: { filter, sort, offset, limit },
          fetchPolicy: 'no-cache'
        })
        const { listEvents } = data

        // Include unauthenticated list events error
        if (
          listEvents.__typename === 'ServerError' ||
          listEvents.__typename === 'UnauthorizedError' ||
          listEvents.__typename === 'UnauthenticatedError' ||
          listEvents.__typename === 'ListEventsErrorOutput'
        ) {
          console.error(listEvents)
          this.serverError = true
          return
        }

        const { events, count } = listEvents
        this.events = events
        this.count = count
      } catch (e) {
        this.serverError = true
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async eventAttributesTypes(client: ApolloClient<NormalizedCacheObject>) {
      try {
        if (this.loading) return
        this.loading = true

        const { data } = await client.query<eventAttributesTypes>({
          query: EVENT_ATTRIBUTES_TYPES,
          fetchPolicy: 'no-cache'
        })

        const { fetchEventTypes } = data

        // Include unauthenticated list events error
        if (
          fetchEventTypes.__typename === 'ServerError' ||
          fetchEventTypes.__typename === 'UnauthorizedError' ||
          fetchEventTypes.__typename === 'UnauthenticatedError'
        ) {
          console.error(fetchEventTypes)
          this.serverError = true
          return
        }

        this.eventTypes = fetchEventTypes.attributes.map((type) => ({
          label: type.name,
          value: type.id
        }))
      } catch (e) {
        this.serverError = true
        console.error(e)
      } finally {
        this.loading = false
      }
    }
  }
})
